/* iter231 — Payment Integrity Diagnostic (READ-ONLY).
   Scans invoices for payment/PO data problems and proposes safe corrections.
   Nothing is auto-corrected. Surfaces Kelly/Erin-style records for the office. */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, RefreshCw, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISSUE_LABELS = {
  non_paid_disposition_with_amount: 'Non-paid disposition carries an amount',
  onsite_on_nonpaid: 'On-site > $0 on a non-paid disposition',
  paid_zero_amount: 'Marked Paid but $0 total paid',
  unpaid_zero_balance: 'Marked Unpaid but $0 balance',
  overpaid: 'Total paid exceeds invoice total',
  cash_but_notes_say_electronic: 'Cash/Check but notes say QuickBooks/card',
  po_conflict: 'Office & tech PO conflict',
  missing_required_po: 'Missing required PO',
  onsite_unknown_legacy: 'On-site collection unknown (legacy)',
};

const PaymentIntegrityDiagnostic = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/billing/payment-integrity-diagnostic`, { withCredentials: true });
      setData(d);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load diagnostic'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const findings = (data?.findings || []).filter((f) => {
    if (!q) return true;
    const s = `${f.customer_name} ${f.invoice_number}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="payment-diagnostic-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-white font-['Outfit'] text-3xl font-bold flex items-center gap-2"><ShieldAlert className="w-7 h-7 text-orange-400" /> Payment Integrity Diagnostic</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Read-only. Nothing is auto-corrected — use Correct Payment / Set PO on each invoice to resolve.</p>
        </div>
        <button data-testid="diag-refresh" onClick={load} disabled={loading} className="inline-flex items-center gap-2 text-sm font-bold px-3 py-2 rounded-lg border border-[#3f3f3f] text-white hover:bg-[#262626]"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Rescan</button>
      </div>

      {data && (
        <div className="rounded-lg border border-orange-600/40 bg-orange-500/10 p-3 mb-4" data-testid="diag-summary">
          <p className="text-orange-100 text-sm font-bold">{data.total_flagged} invoice(s) flagged for review</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.entries(data.counters || {}).map(([k, v]) => (
              <span key={k} className="px-2 py-0.5 rounded text-[11px] font-bold border border-orange-500/40 bg-orange-500/10 text-orange-200">{ISSUE_LABELS[k] || k}: {v}</span>
            ))}
          </div>
        </div>
      )}

      <div className="relative mb-3 max-w-md">
        <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="diag-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a customer (e.g. Kelly, Erin)…" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg pl-9 pr-3 py-2 text-white text-sm" />
      </div>

      <div className="rounded-lg border border-[#262626] overflow-hidden" data-testid="diag-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#141414] text-[#A3A3A3] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Invoice</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2 text-right">Collected On Site</th>
                <th className="px-3 py-2 text-right">Total Paid</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2 text-left">Proposed On-Site</th>
                <th className="px-3 py-2 text-left">Issues</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#A3A3A3]">Scanning…</td></tr>
              ) : findings.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#A3A3A3]">No payment-integrity issues found.</td></tr>
              ) : findings.map((f) => (
                <tr key={f.id} data-testid={`diag-row-${f.id}`} className="border-t border-[#1f1f1f]">
                  <td className="px-3 py-2 text-white">{f.customer_name || '—'}</td>
                  <td className="px-3 py-2 font-mono text-[#D4AF37] whitespace-nowrap">{f.invoice_number}</td>
                  <td className="px-3 py-2 text-right text-white">${f.invoice_total.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-white">{f.stored_amount_collected_on_site == null ? <span className="text-orange-300 text-xs">Unknown</span> : `$${f.stored_amount_collected_on_site.toFixed(2)}`}</td>
                  <td className="px-3 py-2 text-right text-white">${f.total_paid_to_date.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-white">${f.balance_due.toFixed(2)}</td>
                  <td className="px-3 py-2 text-xs" data-testid={`diag-proposed-${f.id}`}>
                    <span className={f.on_site_confident ? 'text-emerald-300' : 'text-orange-300'}>{f.on_site_display}</span>
                    <span className="block text-[10px] text-[#525252]">{f.proposed_basis}</span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {f.issues.map((i) => <span key={i} className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border border-orange-500/40 bg-orange-500/10 text-orange-300">{ISSUE_LABELS[i] || i}</span>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentIntegrityDiagnostic;
